// Utility functions for iframe communication

/**
 * Checks if the current window is inside an iframe
 * @returns {boolean}
 */
export const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

/**
 * Sends a message to the parent window
 * @param {string} type - Message type
 * @param {any} data - Message data
 */
export const sendMessageToParent = (type, data) => {
  if (isInIframe()) {
    window.parent.postMessage({ type, data }, '*');
  }
};

/**
 * Listens for messages from the parent window
 * @param {Function} callback - Callback function to handle messages
 * @returns {Function} - Cleanup function to remove the event listener
 */
export const listenToParentMessages = (callback) => {
  const messageHandler = (event) => {
    // Verify the origin of the message for security
    if (!event.origin.match(/^https?:\/\/(.*\.mediq\.nl|medbis\.localhost)/)) return;

    callback(event.data);
  };

  window.addEventListener('message', messageHandler);

  // Return cleanup function
  return () => window.removeEventListener('message', messageHandler);
};

/**
 * Exposes DOM elements to the parent window
 * @param {string} selector - CSS selector for the elements to expose
 * @param {string} eventType - Type of event to listen for (e.g., 'click', 'change')
 */
export const exposeElementsToParent = (selector, eventType = 'click') => {
  const elements = document.querySelectorAll(selector);

  elements.forEach(element => {
    element.addEventListener(eventType, (event) => {
      sendMessageToParent('elementEvent', {
        type: eventType,
        selector,
        value: element.value,
        text: element.textContent,
        attributes: Array.from(element.attributes).reduce((acc, attr) => {
          acc[attr.name] = attr.value;
          return acc;
        }, {})
      });
    });
  });
};

/**
 * Notifies parent window about URL changes
 */
export const notifyUrlChanges = () => {
  if (!isInIframe()) return;

  // Initial URL notification
  sendMessageToParent('urlChange', {
    url: window.location.href,
    path: window.location.pathname
  });

  // Listen for URL changes
  window.addEventListener('popstate', () => {
    sendMessageToParent('urlChange', {
      url: window.location.href,
      path: window.location.pathname
    });
  });

  // Override pushState and replaceState to catch programmatic navigation
  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function() {
    originalPushState.apply(this, arguments);
    sendMessageToParent('urlChange', {
      url: window.location.href,
      path: window.location.pathname
    });
  };

  window.history.replaceState = function() {
    originalReplaceState.apply(this, arguments);
    sendMessageToParent('urlChange', {
      url: window.location.href,
      path: window.location.pathname
    });
  };
};
